import moment from 'moment';
import { GridCellParams, Operator } from '../models/common';
import { messageStore } from '../stores/MessageStore';
import { ToastVariant, toastStore } from '../stores/ToastStore';
import { needsAttention } from '../models/InvoiceStatusPolicy';

export const getStatusCellClass = ({ value }: GridCellParams) =>
	needsAttention(value) ? 'status-highlight' : '';

export function showDialog(message: string, onConfirm: () => void) {
	messageStore.showConfirm(message, onConfirm);
}

export function showInfoDialog(message: string, title?: string) {
	messageStore.showInfo(message, title);
}

export function showFormDialog(onSubmit: (input: string) => Promise<void>, inputLabel = 'Reason', maxLength = 255) {
	messageStore.showForm(onSubmit, inputLabel, maxLength);
}

export function showToast(message: string, variant: ToastVariant = 'info', duration = 5000) {
	toastStore.show(message, variant, duration);
}

export function showInfoToast(message: string, duration = 5000) {
	showToast(message, 'info', duration);
}

export const toNumber = (value: string | undefined): number => {
	if (value === undefined) {
		return 0;
	}
	return parseFloat(value.replace(/[$,()]/g, ''));
}

const amountFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

const numberFormatter = new Intl.NumberFormat('en-US', {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export const convertUtcToLocalTime = (utcTimestamp: string) => {
	const date = new Date(utcTimestamp);
	return date.toLocaleString();
};

const supportedDateFormats: (string | moment.MomentBuiltinFormat)[] = [
	moment.ISO_8601,
	'YYYY-MM-DD',
	'MM/DD/YYYY',
	'M/D/YYYY',
];
const parseDate = (date: string | Date) =>
	date instanceof Date ? moment(date) : moment(date, supportedDateFormats, true);

export const formatDate = (date?: string | Date, format?: string) => date ? parseDate(date).format(format || 'YYYY-MM-DD') : undefined;
export const formatValidDate = (date: string, format?: string) => parseDate(date).format(format || 'YYYY-MM-DD');
export const formatDateOnly = (isoDate: string) => isoDate ? isoDate.split('T')[0] : '';
export const today = () => new Date().toISOString().substring(0, 10);

export function isGuid(value?: string | null): boolean {
	if (!value) return false;
	const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
	return guidRegex.test(value);
}

export function isUrl(url?: string): boolean {
	if (!url) return false;
	try {
		new URL(url);
		return true;
	} catch {
		return false;
	}
}

// used to display data in the grid
export const getDate = (params: string) => params ? new Date(params + 'T00:00:00') : null; // To prevent javascript from converting the date to local time
export const getDateTime = (params: string) => params ? new Date(params) : null;
export const formatCurrency = (value: number | null | undefined) => amountFormatter.format(value || 0);
export const getString = (params: any) => params?.toString() ?? ''; // since datagrid does not like null values
export const formatNumber = (value: number | null | undefined) => numberFormatter.format(value ?? 0);
export const formatGridDate = (params: string | Date) => params ? parseDate(params).format('MM/DD/YYYY') : null;
export const getTextFromJSON = (params: string) => {
	if (!params) return '';
	let message = params;

	try {
		const parsed = JSON.parse(params);

		if (typeof parsed === 'string') {
			message = parsed;
		} else if (parsed && typeof parsed === 'object') {
			// Handle nested errors structure like { errors: { vendorBillErrors: [...], invoiceErrors: [...] } }
			if (parsed.errors && typeof parsed.errors === 'object') {
				const errorParts: string[] = [];
				for (const key in parsed.errors) {
					const errorArray = parsed.errors[key];
					if (Array.isArray(errorArray) && errorArray.length > 0) {
						errorParts.push(`${key}: ${errorArray.join(', ')}.`);
					}
				}
				if (errorParts.length > 0) {
					return stripHtml(errorParts.join('\n'));
				}
			}

			// Extract meaningful error message from common JSON error formats
			const parsedText = parsed.error || parsed.message;
			if (typeof parsedText === 'string') message = parsedText;
		}
	} catch {
		// Plain text and HTML responses are not JSON.
	}
	return stripHtml(message);
};

export const stripHtml = (html: string): string => {
	let text = html;
	const isHtml = /<!doctype\b|<\/?[a-z][^>]*>/i.test(html);
	if (isHtml) {
		const document = new DOMParser().parseFromString(html, 'text/html');
		document.querySelectorAll('script, style, template, noscript').forEach(node => node.remove());
		document.body.querySelectorAll('br, hr').forEach(node => node.replaceWith('\n'));
		document.body.querySelectorAll('p, div, center, h1, h2, h3, h4, h5, h6, pre, li, tr')
			.forEach(node => node.append('\n'));
		// Use the body so an error page's title does not repeat its heading.
		text = document.body.textContent?.trim() || document.title;
	}

	const lines = text.replace(/\r\n?/g, '\n').split('\n')
		.map(line => line.replace(/\s+/g, ' ').trim())
		.filter(line => line && !(isHtml && /^nginx(?:\/[\d.]+)?$/i.test(line)));
	// Only deduplicate whole adjacent lines; repeated letters and numbers are meaningful.
	return lines.filter((line, index) => line !== lines[index - 1]).join('\n');
};

export const getOperators = (type?: string): Operator[] => {
	switch (type) {
		case 'string':
			return [
				{ value: 'starts with', needsValue: true, filterFn: (value: string, filterValue: string) => value.toLowerCase().startsWith(filterValue.toLowerCase()) },
				{ value: 'equals', needsValue: true, filterFn: (value: string, filterValue: string) => value.toLowerCase() === filterValue.toLowerCase() },
				{ value: 'contains', needsValue: true, filterFn: (value: string, filterValue: string) => value.toLowerCase().includes(filterValue.toLowerCase()) },
				{ value: 'ends with', needsValue: true, filterFn: (value: string, filterValue: string) => value.toLowerCase().endsWith(filterValue.toLowerCase()) },
				{ value: 'is empty', needsValue: false, filterFn: (value: string) => value.trim() === '' },
				{ value: 'is not', needsValue: true, filterFn: (value: string, filterValue: string) => value.toLowerCase() !== filterValue.toLowerCase() },
				{ value: 'is not empty', needsValue: false, filterFn: (value: string) => value.trim() !== '' },
				{ value: 'is any of (,)', needsValue: true, filterFn: (value: string, filterValue: string) => filterValue.split(',').map(v => v.trim()).includes(value) },
			];
		case 'number':
			return [
				{ value: '=', needsValue: true, filterFn: (value: string, filterValue: string) => toNumber(value) === toNumber(filterValue) },
				{ value: '<', needsValue: true, filterFn: (value: string, filterValue: string) => toNumber(value) < toNumber(filterValue) },
				{ value: '>', needsValue: true, filterFn: (value: string, filterValue: string) => toNumber(value) > toNumber(filterValue) },
			];
		case 'date':
			return [
				{ value: 'is', needsValue: true, filterFn: (value: Date, filterValue: string) => moment(value).isSame(filterValue) || moment(value).format('YYYY-MM-DD') === filterValue },
				{ value: 'is after', needsValue: true, filterFn: (value: Date, filterValue: string) => moment(value).isAfter(filterValue) },
				{ value: 'is before', needsValue: true, filterFn: (value: Date, filterValue: string) => moment(value).isBefore(filterValue) },
			];
		case 'boolean':
			return [
				{ value: 'true', needsValue: false, filterFn: (value: boolean) => value === true },
				{ value: 'false', needsValue: false, filterFn: (value: boolean) => value === false },
			];
		default:
			return [];
	}
}

export const isValidEmail = (email: string): { valid: boolean; reason?: string } => {
	const trimmedEmail = email.trim();
	if (trimmedEmail === '') return { valid: true };
	if (trimmedEmail.length > 255) return { valid: false, reason: 'Email address exceeds 255 character limit' };

	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	if (!emailRegex.test(trimmedEmail)) {
		return { valid: false, reason: 'Please enter a valid email address.' };
	}

	return { valid: true };
}

export const detectInvisibleCharacters = (value: string): string[] => {
	const invisibleChars: string[] = [];

	// Common invisible/problematic characters
	const invisibleCharMap = {
		'\u00AD': 'soft hyphen (U+00AD)',
		'\u200B': 'zero-width space (U+200B)',
		'\u200C': 'zero-width non-joiner (U+200C)',
		'\u200D': 'zero-width joiner (U+200D)',
		'\u2060': 'word joiner (U+2060)',
		'\uFEFF': 'zero-width no-break space (U+FEFF)',
		'\u00A0': 'non-breaking space (U+00A0)',
		'\u180E': 'mongolian vowel separator (U+180E)',
		'\u2000': 'en quad (U+2000)',
		'\u2001': 'em quad (U+2001)',
		'\u2002': 'en space (U+2002)',
		'\u2003': 'em space (U+2003)',
		'\u2004': 'three-per-em space (U+2004)',
		'\u2005': 'four-per-em space (U+2005)',
		'\u2006': 'six-per-em space (U+2006)',
		'\u2007': 'figure space (U+2007)',
		'\u2008': 'punctuation space (U+2008)',
		'\u2009': 'thin space (U+2009)',
		'\u200A': 'hair space (U+200A)',
		'\u202F': 'narrow no-break space (U+202F)',
		'\u205F': 'medium mathematical space (U+205F)',
		'\u3000': 'ideographic space (U+3000)'
	};

	for (const [char, description] of Object.entries(invisibleCharMap)) {
		if (value.includes(char)) {
			invisibleChars.push(description);
		}
	}

	return invisibleChars;
}

// Clean text by removing invisible characters that can break APIs
// export const cleanInvisibleCharacters = (value: string): string => {
// 	if (!value) return value;

// 	// Remove all the problematic invisible characters
// 	return value
// 		.replace(/[\u00AD\u200B\u200C\u200D\u2060\uFEFF\u180E]/g, '') // Remove zero-width and soft hyphens
// 		.replace(/[\u2000-\u200A\u202F\u205F\u3000]/g, ' ') // Replace fancy spaces with regular space
// 		.replace(/\u00A0/g, ' ') // Replace non-breaking space with regular space
// 		.trim(); // Remove leading/trailing whitespace
// }

// // React event handler to clean paste events
// export const handleCleanPaste = (event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
// 	event.preventDefault();
// 	const pasteData = event.clipboardData.getData('text/plain');
// 	const cleanedData = cleanInvisibleCharacters(pasteData);

// 	// Insert cleaned text at cursor position
// 	const target = event.target as HTMLInputElement | HTMLTextAreaElement;
// 	const start = target.selectionStart || 0;
// 	const end = target.selectionEnd || 0;
// 	const currentValue = target.value;

// 	const newValue = currentValue.substring(0, start) + cleanedData + currentValue.substring(end);
// 	target.value = newValue;

// 	// Set cursor position after inserted text
// 	const newCursorPos = start + cleanedData.length;
// 	target.setSelectionRange(newCursorPos, newCursorPos);

// 	// Trigger input event to update React state
// 	const inputEvent = new Event('input', { bubbles: true });
// 	target.dispatchEvent(inputEvent);
// }

// // Clean input on every keystroke (for direct typing of problematic chars)
// export const handleCleanInput = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
// 	const target = event.target as HTMLInputElement | HTMLTextAreaElement;
// 	const originalValue = target.value;
// 	const cleanedValue = cleanInvisibleCharacters(originalValue);

// 	// Only update if cleaning actually changed something
// 	if (originalValue !== cleanedValue) {
// 		const cursorPos = target.selectionStart || 0;
// 		target.value = cleanedValue;
// 		// Maintain cursor position (accounting for removed characters)
// 		const removedChars = originalValue.length - cleanedValue.length;
// 		target.setSelectionRange(Math.max(0, cursorPos - removedChars), Math.max(0, cursorPos - removedChars));
// 	}
// }
